import React, { Suspense } from 'react';
import { Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import AuthGuard from '../components/AuthGuard';
import RequirePermission from '../components/RequirePermission';
import { RouteLoadingShell } from '../components/RouteLoadingShell';

// Lazy-loaded page components
const AcquisitionGrowth = React.lazy(() => import('../features/analytics/pages/AcquisitionGrowth'));
const ProductEngagement = React.lazy(() => import('../features/analytics/pages/ProductEngagement'));
const RevenueMarketing = React.lazy(() => import('../features/analytics/pages/RevenueMarketing'));
const GrowthBusinessOverview = React.lazy(() => import('../features/growth/pages/GrowthBusinessOverview'));
const TrustAnalyticsOverview = React.lazy(() => import('../features/trust/pages/TrustAnalyticsOverview'));
const AccountDashboard = React.lazy(() => import('../features/trust/pages/AccountDashboard'));
const ModerationDashboard = React.lazy(() => import('../features/trust/pages/ModerationDashboard'));
const EnforcementRulesetPage = React.lazy(() => import('../features/trust/pages/EnforcementRulesetPage'));
const DataOperationsOverview = React.lazy(() => import('../features/data-operations/pages/DataOperationsOverview'));
const Glossary = React.lazy(() => import('../features/analytics/pages/Glossary'));
const ManageAthletes = React.lazy(() => import('../features/athlete/pages/ManageAthletes'));
const ViewAthlete = React.lazy(() => import('../features/athlete/pages/ViewAthletes'));
const ManageColleges = React.lazy(() => import('../features/college/pages/ManageColleges'));
const ViewCollege = React.lazy(() => import('../features/college/pages/ViewCollege'));
const ManageCoaches = React.lazy(() => import('../features/coach/pages/ManageCoaches'));
const ViewCoach = React.lazy(() => import('../features/coach/pages/ViewCoach'));
const CreateCoach = React.lazy(() => import('../features/coach/pages/CreateCoach'));
const NotFound = React.lazy(() => import('../features/NotFound'));
const Waitlist = React.lazy(() => import('../features/waitlist/Waitlist'));
const ReviewCoach = React.lazy(() => import('../features/coach/pages/ReviewCoach'));
const ManageMedia = React.lazy(() => import('../features/media/ManageMedia'));
const ViewMedia = React.lazy(() => import('../features/media/ViewMedia'));
const TextAppealList = React.lazy(() => import('../features/moderation/TextAppealList'));
const TextAppealDetail = React.lazy(() => import('../features/moderation/TextAppealDetail'));
const ReportList = React.lazy(() => import('../features/moderation/ReportList'));
const ReportDetail = React.lazy(() => import('../features/moderation/ReportDetail'));
const BannedSuspendedUsers = React.lazy(() => import('../features/user/pages/BannedSuspendedUsers'));
const DeletedDeactivatedUsers = React.lazy(() => import('../features/user/pages/DeletedDeactivatedUsers'));
const SystemSettings = React.lazy(() => import('../features/settings/pages/SystemSettings'));
const ManageProfile = React.lazy(() => import('../features/settings/pages/ManageProfile'));
const ManageTemplates = React.lazy(() => import('../features/templates/pages/ManageTemplates'));
const EmailTemplates = React.lazy(() => import('../features/templates-export/pages/EmailTemplates'));
const ManageBrandRecruiterRequests = React.lazy(() => import('../features/brand-recruiter-requests/pages/ManageBrandRecruiterRequests'));
const ViewBrandRecruiter = React.lazy(() => import('../features/brand-recruiter-requests/pages/ViewBrandRecruiter'));
const ManageOrganizations = React.lazy(() => import('../features/organizations/pages/ManageOrganizations'));
const CreateOrganization = React.lazy(() => import('../features/organizations/pages/CreateOrganization'));
const ManageFeatureFlags = React.lazy(() => import('../features/feature-flags/pages/ManageFeatureFlags'));
const PlatformEngineeringOverview = React.lazy(() => import('../features/platform/pages/PlatformEngineeringOverview'));
const ManageScouts = React.lazy(() => import('../features/scout/pages/ManageScouts'));
const ReviewScout = React.lazy(() => import('../features/scout/pages/ReviewScout'));
const AdminStatManagement = React.lazy(() => import('../features/sport-stats/pages/AdminStatManagement'));
const AdminAllStats = React.lazy(() => import('../features/sport-stats/pages/AdminAllStats'));
const CoachImport = React.lazy(() => import('../features/coach-import/pages/CoachImport'));
const CoachImportReview = React.lazy(() => import('../features/coach-import/pages/CoachImportReview'));
const AthleticProgramLookup = React.lazy(() => import('../features/athletic-program/pages/ManageAthleticProgramLookup'));
const ManageAthleticPrograms = React.lazy(() => import('../features/athletic-program/pages/ManageAthleticPrograms'));
const ViewAthleticProgram = React.lazy(() => import('../features/athletic-program/pages/ViewAthleticProgram'));
const ManageParents = React.lazy(() => import('../features/parents/pages/ManageParents'));
const ViewCreditCoupon = React.lazy(() => import('../features/credit-coupons/pages/ViewCreditCoupon'));
const ViewParent = React.lazy(() => import('../features/parents/pages/ViewParent'));
const AiCoachLibrary = React.lazy(() => import('../features/ai-coach/pages/Library'));
const AiCoachGaps = React.lazy(() => import('../features/ai-coach/pages/Gaps'));
const AiCoachCoaches = React.lazy(() => import('../features/ai-coach/pages/Coaches'));
const AiCoachGuidance = React.lazy(() => import('../features/ai-coach/pages/Guidance'));
const AiCoachOverview = React.lazy(() => import('../features/ai-coach/pages/AiCoachOverview'));
const AiCoachQuestions = React.lazy(() => import('../features/ai-coach/pages/Questions'));
const AiCoachTestRuns = React.lazy(() => import('../features/ai-coach/pages/TestRuns'));
const AiCoachTestRun = React.lazy(() => import('../features/ai-coach/pages/TestRun'));
const AiCoachTestRunCompare = React.lazy(() => import('../features/ai-coach/pages/TestRunCompare'));
const AiCoachUsage = React.lazy(() => import('../features/ai-coach/pages/Usage'));

const PageLoader: React.FC = () => <RouteLoadingShell label="Loading page" />;

const LegacyMediaViewRedirect: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/v2/moderation/media/view/${id}`} replace />;
};

const LegacyAthleteEditRedirect: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/v2/manage-athlete/view/${id}`} replace />;
};

/** The prompt bank's old addresses, from before it became Test Questions / Test Runs. */
const LegacyAiCoachRunRedirect: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/v2/ai-coach/test-runs/${id}`} replace />;
};

const LegacyAiCoachCompareRedirect: React.FC = () => {
  const { search } = useLocation();
  return <Navigate to={`/v2/ai-coach/test-runs/compare${search}`} replace />;
};


const AppRouter: React.FC = () => {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route element={<AuthGuard />}>
          {/* Analytics — requires analytics:read */}
          <Route element={<RequirePermission permission="analytics:read" />}>
            <Route path="/growth" element={<GrowthBusinessOverview />} />
            <Route path="/analytics" element={<AcquisitionGrowth />} />
            <Route path="/analytics/demographics" element={<Navigate to="/v2/analytics?section=demographics" replace />} />
            <Route path="/analytics/growth" element={<Navigate to="/v2/analytics?section=trends" replace />} />
            <Route path="/analytics/monetization" element={<RevenueMarketing />} />
            <Route path="/analytics/sendouts" element={<Navigate to="/v2/analytics/monetization?section=sendouts" replace />} />
            <Route path="/credit-coupons" element={<Navigate to="/v2/analytics/monetization?section=coupons" replace />} />
            <Route path="/credit-coupons/:promotionCodeId" element={<ViewCreditCoupon />} />
            <Route path="/analytics/engagement" element={<ProductEngagement />} />
            <Route path="/analytics/sports" element={<Navigate to="/v2/analytics/engagement?section=profile" replace />} />
            <Route path="/analytics/profile-stats-insights" element={<Navigate to="/v2/analytics/engagement?section=profile" replace />} />
            <Route path="/analytics/bookmarks" element={<Navigate to="/v2/analytics/engagement?section=bookmarks" replace />} />
            <Route path="/analytics/clip-creator" element={<Navigate to="/v2/analytics/engagement?section=clips" replace />} />
            <Route path="/analytics/chat" element={<Navigate to="/v2/analytics/engagement?section=messages" replace />} />
            <Route
              path="/analytics/athlete-searches"
              element={<Navigate to="/v2/analytics/engagement?section=athlete-searches" replace />}
            />
            <Route
              path="/analytics/college-search"
              element={<Navigate to="/v2/analytics/engagement?section=college-search" replace />}
            />
          </Route>

          {/* User management — requires users:read */}
          <Route element={<RequirePermission permission="users:read" />}>
            <Route path="/manage-athlete" element={<ManageAthletes />} />
            <Route path="/manage-athlete/view/:id" element={<ViewAthlete />} />
            <Route
              path="/manage-athlete/edit/:id"
              element={<LegacyAthleteEditRedirect />}
            />
            <Route path="/manage-parents" element={<ManageParents />} />
            <Route path="/manage-parents/view/:id" element={<ViewParent />} />
            <Route
              path="/manage-coach"
              element={<Navigate to="/v2/manage-coach/pending" replace />}
            />
            <Route path="/manage-coach/view/:id" element={<ViewCoach />} />
            <Route path="/manage-coach/edit/:id" element={<CreateCoach />} />
            <Route path="/manage-coach/create" element={<CreateCoach />} />
            <Route path="/manage-coach/pending" element={<ManageCoaches />} />
            <Route path="/manage-coach/approved" element={<ManageCoaches />} />
            <Route path="/manage-coach/stalled" element={<ManageCoaches />} />
            <Route path="/manage-coach/rejected" element={<ManageCoaches />} />
            <Route path="/manage-coach/all" element={<ManageCoaches />} />
            <Route path="/manage-coach/onboarding/:user_id" element={<ReviewCoach />} />
            <Route path="/manage-brand-recruiters" element={<ManageBrandRecruiterRequests />} />
            <Route path="/manage-brand-recruiters/view/:userId" element={<ViewBrandRecruiter />} />
            <Route path="/organizations" element={<ManageOrganizations />} />
            <Route path="/organizations/create" element={<CreateOrganization />} />
            <Route path="/organizations/edit/:userId" element={<CreateOrganization />} />
            <Route path="/manage-scout" element={<ManageScouts />} />
            <Route path="/manage-scout/onboarding/:user_id" element={<ReviewScout />} />
            <Route path="/waitlist" element={<Waitlist />} />
          </Route>

          {/* Trust overview — analytics across account + moderation */}
          <Route
            element={
              <RequirePermission anyOf={['users:read', 'moderation:read']} />
            }
          >
            <Route path="/trust/overview" element={<TrustAnalyticsOverview />} />
            <Route path="/trust" element={<Navigate to="/v2/trust/overview" replace />} />
          </Route>

          {/* Account Approvals dashboard */}
          <Route element={<RequirePermission permission="users:read" />}>
            <Route path="/trust/accounts" element={<AccountDashboard />} />
          </Route>

          {/* Moderation dashboard + queues */}
          <Route element={<RequirePermission permission="moderation:read" />}>
            <Route path="/trust/moderation" element={<ModerationDashboard />} />
            <Route
              path="/trust/moderation/ruleset"
              element={<EnforcementRulesetPage />}
            />
            <Route path="/moderation" element={<Navigate to="/v2/trust/moderation" replace />} />
            <Route path="/moderation/reports" element={<ReportList />} />
            <Route path="/moderation/reports/:id" element={<ReportDetail />} />
            <Route path="/moderation/text-appeals" element={<TextAppealList />} />
            <Route path="/moderation/text-appeals/:id" element={<TextAppealDetail />} />
            <Route path="/moderation/media" element={<ManageMedia />} />
            <Route path="/moderation/media/view/:id" element={<ViewMedia />} />
            {/* Legacy redirects */}
            <Route path="/manage-media" element={<Navigate to="/v2/moderation/media" replace />} />
            <Route path="/manage-media/view/:id" element={<LegacyMediaViewRedirect />} />
          </Route>

          {/* Own admin profile — any authenticated admin */}
          <Route path="/manage-profile" element={<ManageProfile />} />

          {/* Settings — requires settings:manage */}
          <Route element={<RequirePermission permission="settings:manage" />}>
            <Route path="/platform" element={<PlatformEngineeringOverview />} />
            <Route path="/glossary" element={<Glossary />} />
            <Route path="/bans-suspensions" element={<BannedSuspendedUsers />} />
            <Route path="/deleted-deactivated-users" element={<DeletedDeactivatedUsers />} />
            <Route
              path="/deleted-cancelled-users"
              element={<Navigate to="/bans-suspensions" replace />}
            />
            <Route path="/system-settings" element={<SystemSettings />} />
            <Route path="/feature-flags" element={<ManageFeatureFlags />} />
            <Route path="/templates-export" element={<EmailTemplates />} />
            <Route path="/manage-templates" element={<ManageTemplates />} />
            <Route path="/manage-templates/:channel" element={<ManageTemplates />} />
          </Route>

          {/* Marketing export — requires marketing:export (hub tab also gated client-side) */}
          <Route element={<RequirePermission permission="marketing:export" />}>
            <Route
              path="/marketing-export"
              element={<Navigate to="/v2/analytics/monetization?section=marketing" replace />}
            />
          </Route>

          {/* Admin-only domains — requires admin:manage */}
          <Route element={<RequirePermission permission="admin:manage" />}>
            <Route path="/data" element={<DataOperationsOverview />} />
            <Route path="/manage-college" element={<ManageColleges />} />
            <Route path="/manage-college/view/:slug" element={<ViewCollege />} />
            <Route path="/manage-sport-stats" element={<AdminStatManagement />} />
            <Route path="/manage-all-stats" element={<AdminAllStats />} />
            <Route path="/coach-import" element={<CoachImport />} />
            <Route path="/coach-import/:batchId/review" element={<CoachImportReview />} />
            <Route path="/athletic-program-lookup" element={<AthleticProgramLookup />} />
            <Route path="/athletic-programs" element={<ManageAthleticPrograms />} />
            <Route path="/ai-coach" element={<AiCoachOverview />} />
            <Route path="/ai-coach/library" element={<AiCoachLibrary />} />
            <Route path="/ai-coach/gaps" element={<AiCoachGaps />} />
            <Route path="/ai-coach/coaches" element={<AiCoachCoaches />} />
            <Route path="/ai-coach/guidance" element={<AiCoachGuidance />} />
            <Route path="/ai-coach/questions" element={<AiCoachQuestions />} />
            <Route path="/ai-coach/test-runs" element={<AiCoachTestRuns />} />
            <Route path="/ai-coach/test-runs/compare" element={<AiCoachTestRunCompare />} />
            <Route path="/ai-coach/test-runs/:id" element={<AiCoachTestRun />} />
            <Route path="/ai-coach/usage" element={<AiCoachUsage />} />
            {/* Legacy redirects */}
            <Route path="/ai-coach/prompts" element={<Navigate to="/v2/ai-coach/questions" replace />} />
            <Route path="/ai-coach/prompts/runs/:id" element={<LegacyAiCoachRunRedirect />} />
            <Route path="/ai-coach/prompts/compare" element={<LegacyAiCoachCompareRedirect />} />
            <Route path="/athletic-programs/:slug" element={<ViewAthleticProgram />} />
          </Route>

          {/* Fallback */}
          <Route path="/marketing-videos" element={<NotFound />} />
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    </Suspense>
  );
};

export default AppRouter;
